/**
 * SignifyConfigurationSidebar
 *
 * Presentational drawer for a Signify punch-out configuration: distributor and
 * product pickers, the API-to-line-item mapping table, and the create/open/insert
 * actions. All state arrives as props from `useSignifyConfigurationSidebar`; this
 * component owns no state at all — the drag width is held by `uiState`, which
 * outlives it, clamps against the viewport and persists per user across reopens
 * and reloads. The drag handle is drawn by `drawerDraggerStyle`, which makes it
 * visible.
 */

import { FC, useMemo } from 'react';
import {
    Drawer,
    Button,
    Input,
    Switch,
    Typography,
    Alert,
    Collapse,
    Table,
    Select,
    theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExportOutlined, CheckOutlined, ClearOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import FlexBox from '@/components/atoms/FlexBox';
import FlexCol from '@/components/atoms/FlexCol';
import FlexDivider from '@/components/atoms/FlexDivider';
import { drawerDraggerStyle } from '@/components/atoms/drawerDraggerStyle';
import SignifyDistributorSelector from '../SignifyProductModal/components/SignifyDistributorSelector';
import SignifyProductSearch from '../SignifyProductModal/components/SignifyProductSearch';
import {
    panelMaxWidth,
    panelViewportClamp,
    setPanelWidth,
    uiState,
} from '@/store/uiState';
import type { SignifyConfigurationSidebarProps, MappingTableRow } from './index.types';

const { Text } = Typography;

const SignifyConfigurationSidebar: FC<SignifyConfigurationSidebarProps> = ({
    open,
    onClose,
    mode,
    customerId,
    customerIdDisabled,
    onCustomerIdChange,
    createSession,
    onCreateSessionChange,
    selectedDistributor,
    distributorSearchValue,
    filteredDistributors,
    filteredDistributorsCount,
    totalDistributors,
    distributorSearchTerm,
    loadingDistributors,
    onDistributorSearch,
    onDistributorSelect,
    onDistributorClear,
    onRefreshDistributors,
    selectedProduct,
    configurableProductId,
    productSearchValue,
    productOptions,
    searchingProducts,
    onProductSearch,
    onProductSelect,
    onProductClear,
    catalogCode,
    onCatalogCodeChange,
    punchOutUrl,
    sessionId,
    onCreateConfiguration,
    onOpenPunchOut,
    onInsert,
    loadingCreate,
    loadingRetrieve,
    loadingInsert,
    error,
    distributorWarning,
    insertDisabledReason,
    saveRecord,
    onSaveRecordChange,
    canWriteRecords,
    hasActiveSession,
    onClearConfiguration,
    mappingTableData,
    availableLineItemFields,
    onMappingChange
}) => {
    const { token } = theme.useToken();
    const { width } = useSnapshot(uiState.signifyConfigurationSidebar, { sync: true });

    const isBusy = loadingCreate || loadingRetrieve || loadingInsert;

    const mappingColumns: ColumnsType<MappingTableRow> = useMemo(() => [
        {
            title: 'API Field',
            dataIndex: 'apiFieldDisplay',
            onCell: (row: MappingTableRow) => row.sectionHeader ? { colSpan: 2 } : {},
            render: (text: string, row: MappingTableRow) => {
                if (row.sectionHeader) {
                    return <Text>{text}</Text>;
                }
                return (
                    <Text>
                        {text}
                        {row.apiFieldDisplay !== row.apiField && <><br /><Text type="secondary"><small>{row.apiField}</small></Text></>}
                    </Text>
                );
            },
        },
        {
            title: 'Line Item Field',
            dataIndex: 'lineItemField',
            onCell: (row: MappingTableRow) => row.sectionHeader ? { colSpan: 0 } : {},
            render: (_: string, row: MappingTableRow) => {
                if (row.sectionHeader) return null;
                if (!row.editable) {
                    const showId = row.lineItemFieldDisplay && row.lineItemFieldDisplay !== row.lineItemField;
                    return (
                        <Text>
                            {row.lineItemFieldDisplay || row.lineItemField}
                            {showId && <><br /><Text type="secondary"><small>{row.lineItemField}</small></Text></>}
                        </Text>
                    );
                }
                const mappedFieldIdsForOtherRows = new Set(
                    mappingTableData
                        .filter(r => r.key !== row.key && r.lineItemField)
                        .map(r => r.lineItemField)
                );
                const options = availableLineItemFields
                    .filter(f => f.fieldId === row.lineItemField || !mappedFieldIdsForOtherRows.has(f.fieldId))
                    .map(f => ({
                        value: f.fieldId,
                        label: f.label,
                    }));
                return (
                    <Select
                        aria-label={`Mapping for ${row.apiField}`}
                        value={row.lineItemField || undefined}
                        options={options}
                        onChange={(value) => onMappingChange(row.apiField, value ?? null)}
                        allowClear
                        showSearch={{ optionFilterProp: 'label' }}
                        variant="borderless"
                        size="small"
                        style={{ width: '100%' }}
                        optionRender={(option) => {
                            const fieldId = option.value as string;
                            const label = option.label as string;
                            if (label !== fieldId) {
                                return <>{label} <Text type="secondary"><small>{fieldId}</small></Text></>;
                            }
                            return <>{fieldId}</>;
                        }}
                    />
                );
            },
        },
    ], [availableLineItemFields, mappingTableData, onMappingChange]);

    return (
        <Drawer
            title={<Text>Configure with Signify</Text>}
            open={open}
            onClose={onClose}
            size={width}
            maxSize={panelMaxWidth('signifyConfigurationSidebar')}
            resizable={{ onResize: (next) => setPanelWidth('signifyConfigurationSidebar', next) }}
            styles={{
                dragger: drawerDraggerStyle(token),
                wrapper: { maxWidth: panelViewportClamp('signifyConfigurationSidebar') },
            }}
        >
            <FlexCol>
                <FlexCol noGap>
                    <Text>Customer ID</Text>
                    <Input
                        aria-label="Signify customer ID"
                        value={customerId}
                        onChange={(event) => onCustomerIdChange(event.target.value)}
                        disabled={customerIdDisabled}
                    />
                </FlexCol>

                <SignifyDistributorSelector
                    selectedDistributor={selectedDistributor}
                    distributorSearchValue={distributorSearchValue}
                    loading={isBusy}
                    loadingDistributors={loadingDistributors}
                    filteredDistributors={filteredDistributors}
                    filteredDistributorsCount={filteredDistributorsCount}
                    totalDistributors={totalDistributors}
                    distributorSearchTerm={distributorSearchTerm}
                    onDistributorSearch={onDistributorSearch}
                    onDistributorSelect={onDistributorSelect}
                    onDistributorClear={onDistributorClear}
                    onRefreshDistributors={onRefreshDistributors}
                />

                {distributorWarning && (
                    <Alert
                        type="warning"
                        description={distributorWarning}
                        showIcon={false}
                        style={{ width: '100%' }}
                    />
                )}

                <FlexBox stretch wrap alignStart>
                    {mode === 'product' ? (
                        <FlexCol noGap>
                            <SignifyProductSearch
                                productSearchValue={productSearchValue}
                                productOptions={productOptions}
                                searchingProducts={searchingProducts}
                                loading={isBusy}
                                onProductSearch={onProductSearch}
                                onProductSelect={onProductSelect}
                                onProductClear={onProductClear}
                            />
                            <Text type={selectedProduct && !configurableProductId ? "danger" : "secondary"}>
                                <small>
                                    {selectedProduct && configurableProductId && `Product ID: ${configurableProductId}`}
                                    {selectedProduct && !configurableProductId && "This product is not configurable."}
                                    &nbsp;
                                </small>
                            </Text>
                        </FlexCol>
                    ) : (
                        <FlexCol noGap>
                            <Text>Catalog Code</Text>
                            <Input
                                aria-label="Signify catalog code"
                                value={catalogCode}
                                onChange={(event) => onCatalogCodeChange(event.target.value)}
                                placeholder="Enter catalog code"
                            />
                        </FlexCol>
                    )}
                </FlexBox>

                {mode === 'catalog' && (
                    <FlexBox>
                        <Switch
                            aria-label="Create configuration session"
                            checked={createSession}
                            onChange={(value) => onCreateSessionChange(value)}
                        />
                        <Text>Create session</Text>
                    </FlexBox>
                )}

                {canWriteRecords && (
                    <FlexBox>
                        <Switch
                            aria-label="Save entity record"
                            checked={saveRecord}
                            onChange={onSaveRecordChange}
                            disabled={isBusy}
                        />
                        <Text>Also save as record</Text>
                    </FlexBox>
                )}
                <Collapse
                    size="small"
                    bordered={false}
                    style={{ width: '100%' }}
                    items={[{
                        key: 'advanced',
                        label: <Text>Advanced</Text>,
                        children: (
                            <Table
                                aria-label="Signify field mappings"
                                dataSource={mappingTableData}
                                columns={mappingColumns}
                                pagination={false}
                                size="small"
                                rowKey="key"
                                onRow={(row) => {
                                    if (row.sectionHeader) return { style: { background: token.colorFill } };
                                    if (row.customized) return { style: { background: token.colorInfoBg } };
                                    return {};
                                }}
                            />
                        ),
                    }]}
                />
                <FlexDivider />
                {error && (
                    <Alert
                        type="error"
                        description={error}
                        showIcon={false}
                        style={{ width: '100%' }}
                        closable
                    />
                )}
                <FlexBox stretch wrap>
                    <Button
                        size="large"
                        type="link"
                        danger
                        disabled={isBusy || !hasActiveSession}
                        icon={<ClearOutlined />}
                        onClick={onClearConfiguration}
                        aria-label="Start new Signify configuration"
                    >
                        Clear
                    </Button>
                    <Button
                        size="large"
                        type={punchOutUrl ? 'link' : 'primary'}
                        icon={<ExportOutlined />}
                        onClick={punchOutUrl ? onOpenPunchOut : onCreateConfiguration}
                        loading={loadingCreate}
                        disabled={!punchOutUrl && (isBusy || (mode === 'product' && !configurableProductId))}
                        aria-label={punchOutUrl ? 'Open Signify configurator' : 'Start Signify configuration'}
                    >
                        {mode === 'product' || punchOutUrl ? 'Open Configurator' : 'Auto-Configure'}
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        icon={<CheckOutlined />}
                        onClick={onInsert}
                        loading={loadingInsert}
                        disabled={isBusy || Boolean(insertDisabledReason) || !sessionId}
                        aria-label="Insert Signify configuration into table"
                    >
                        Insert
                    </Button>
                </FlexBox>
                <Text type="secondary">
                    {insertDisabledReason && insertDisabledReason}
                </Text>
            </FlexCol>
        </Drawer>
    );
};

export default SignifyConfigurationSidebar;
